/**
 * Dynamic Grid
 *
 * To opgaver:
 *  1. Hjælpelinjer i theme editoren, så merchanten kan se selve griddet.
 *  2. Parallax-fallback for browsere uden scroll-drevne CSS-animationer
 *     (i praksis Firefox). Moderne browsere klarer det helt i CSS.
 */
(function () {
  'use strict';

  if (!('customElements' in window)) return;

  var supportsScrollTimeline =
    'CSS' in window &&
    typeof CSS.supports === 'function' &&
    CSS.supports('animation-timeline', 'view()');

  class DynamicGrid extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      // Elementet kan blive opgraderet, før dets celler er parset. Vent til
      // dokumentet er færdigt, ellers finder vi ingen celler.
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this._init(), { once: true });
      } else {
        this._init();
      }
    }

    _init() {
      this._section = this.closest('.shopify-section') || this.parentNode;

      if (window.Shopify && window.Shopify.designMode) {
        this._bindEditorEvents();
      }

      if (!supportsScrollTimeline) {
        this._setupParallaxFallback();
      }
    }

    disconnectedCallback() {
      this._stopParallax();

      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
    }

    /* ------------------------------------------------------------------
       Theme editor
       ------------------------------------------------------------------ */

    _bindEditorEvents() {
      if (!this._section) return;

      this._section.addEventListener('shopify:section:select', () => {
        this.classList.add('is-editor-selected');
      });

      this._section.addEventListener('shopify:section:deselect', () => {
        this.classList.remove('is-editor-selected');
      });

      this.addEventListener('shopify:block:select', (event) => {
        var cell = event.target.closest('.DynamicGrid__Cell');
        if (cell) cell.classList.add('is-editor-selected');
        this.classList.add('is-editor-selected');
      });

      this.addEventListener('shopify:block:deselect', (event) => {
        var cell = event.target.closest('.DynamicGrid__Cell');
        if (cell) cell.classList.remove('is-editor-selected');
      });
    }

    /* ------------------------------------------------------------------
       Parallax-fallback
       ------------------------------------------------------------------ */

    _setupParallaxFallback() {
      if (!this._section || !('IntersectionObserver' in window)) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      // Hastigheden læses én gang — getComputedStyle per frame er for dyrt.
      this._cells = Array.prototype.slice
        .call(this.querySelectorAll('.DynamicGrid__Cell[data-dg-parallax]'))
        .map((element) => ({
          element: element,
          speed: parseFloat(
            getComputedStyle(element).getPropertyValue('--dg-parallax-speed')
          ) || 0,
          horizontal: element.getAttribute('data-dg-parallax') === 'horizontal'
        }))
        .filter((cell) => cell.speed !== 0);

      if (!this._cells.length) return;

      this._observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this._startParallax();
          } else {
            this._stopParallax();
          }
        },
        { rootMargin: '10% 0px' }
      );

      this._observer.observe(this._section);
    }

    _startParallax() {
      if (this._frame) return;

      var tick = () => {
        this._updateParallax();
        this._frame = window.requestAnimationFrame(tick);
      };

      this._frame = window.requestAnimationFrame(tick);
    }

    _stopParallax() {
      if (!this._frame) return;
      window.cancelAnimationFrame(this._frame);
      this._frame = null;
    }

    /**
     * Spejler CSS'ens animation-range: cover — progress går fra 0, når
     * sektionens top rammer bunden af viewporten, til 1, når sektionens
     * bund forlader toppen. Offset måles i vmin som i keyframesene.
     */
    _updateParallax() {
      var rect = this._section.getBoundingClientRect();
      var viewportHeight = window.innerHeight;
      var travel = rect.height + viewportHeight;

      if (travel <= 0) return;

      var progress = Math.max(0, Math.min(1, (viewportHeight - rect.top) / travel));
      var vmin = Math.min(window.innerWidth, viewportHeight) / 100;

      this._cells.forEach((cell) => {
        var offset = (progress * 2 - 1) * cell.speed * 5 * vmin;

        cell.element.style.transform = cell.horizontal
          ? 'translate3d(' + offset.toFixed(2) + 'px, 0, 0)'
          : 'translate3d(0, ' + offset.toFixed(2) + 'px, 0)';
      });
    }
  }

  if (!window.customElements.get('dynamic-grid')) {
    window.customElements.define('dynamic-grid', DynamicGrid);
  }
})();
